/* ==========================================================================================
   EcoVessel 2026 (forked from the Blenders Online build) · ev-main.js · shared behaviours for the design system (looks: assets/ev-base.css)
   No dependencies. Loaded once, deferred, from layout/theme.liquid. Safe to load twice (the second copy does
   nothing). Every feature is opt-in by data attribute, binds each element once, and re-binds when the theme
   editor re-renders a section (shopify:section:load / :unload).
   Under prefers-reduced-motion everything below is off or instant.
   Section-specific JavaScript does NOT go here: put it in the section's own deferred script.

   PUBLIC HOOKS
   ------------
   html.ev-js                      set as soon as this file runs
   html.ev-scrolled                set while the page is scrolled more than 40px
   html.ev-io + .ev-in-view        .shopify-section gets .ev-in-view while it is within 200px of the screen
                                   (ev-base.css pauses the CSS animations of every section that is not).
                                   A section holding a sticky or fixed piece that shows while the section is
                                   off screen opts out with data-ev-keep-moving on any element inside it.

   [data-ev-reveal]                rise 24px and fade in when it scrolls into view. Values: (none) | stagger
                                   (its children in turn) | card (the product card's own) | left | right | zoom |
                                   fade. Stagger siblings with style="--i: 1". Content is visible by default:
                                   only what is still below the fold is hidden, then revealed. Once seen it
                                   keeps the class .ev-in (a hook for your own CSS) and fires ev:reveal.
                                   Contract: docs/notes/look-motion.md.
   [data-ev-rail]                  slider. Inside it: .ev-rail__track (or [data-ev-track]), [data-ev-prev],
                                   [data-ev-next], [data-ev-dots] (filled with one dot per page) and / or
                                   [data-ev-rail-count] (its value is the pattern, default "[first] - [last] / [total]").
                                   Sets .is-still when there is nothing to scroll, and --ev-rail-p (0..1) on
                                   the rail only when it asks with data-ev-rail-p (a style write per scroll
                                   frame costs a restyle, so it is opt-in). Event: ev:rail:change
                                   { progress, page, pages }.
   [data-ev-tabs]                  tabs. Inside it [role="tab"][aria-controls] and [role="tabpanel"].
                                   Left / Right / Home / End move. Event: ev:tabs:change { tab, panel }.
                                   The tab row gets .is-more-start / .is-more-end while tabs are out of sight.
   [data-ev-accordion]             a group of <details>. Opens and closes smoothly where CSS alone cannot.
                                   data-ev-accordion="single" keeps one row open at a time.
   [data-ev-marquee]               logo strip > .ev-marquee__track. Holds on the first item, then eases into a
                                   seamless loop; eases to a stop on hover or focus; still when motion is reduced.
                                   data-ev-speed="48" (px/s)  data-ev-hold="2500" (ms)
   [data-ev-count="1250"]          counts up to a REAL number written in the markup, once, when seen.
                                   Decimals are kept (4.8 counts to 4.8). data-ev-prefix / data-ev-suffix.
   [data-ev-parallax="0.12"]       sets --ev-py (px) on the element as the page scrolls; ev-base.css applies it
   <button data-ev-yt="VIDEO_ID">  lite YouTube: swaps itself for the player on click
   [data-ev-qty="1"] / ="-1"       a + / - button inside .ev-qty: steps the number input beside it
   [data-ev-ship]                  the free-shipping bar (snippets/ev-cart-freeship.liquid): a redrawn bar fills
                                   smoothly from the last value; a small burst once when free shipping unlocks
   [data-ev-cart-count]            text = number of items in the cart (add data-ev-hide-empty to hide it at 0)
   [data-ev-cart-total]            text = the cart total, formatted
   form[data-ev-quick-add]         a real <form action="/cart/add"> (hidden id + quantity): posted in place.
                                   Inside it a [type=submit] (gets .is-loading, then .is-added for 2.4s: green,
                                   "Added to cart") and an optional [data-ev-cart-msg] for an error.
                                   data-msg-ok / data-msg-view / data-msg-error: the words of the toast.
                                   Then: Dawn's cart drawer or cart notification opens if the page has one;
                                   otherwise the count bubbles update and a toast says so.
   [data-ev-quick-add][data-ev-variant] + [data-ev-add] button
                                   the same without a form (data-ev-add-qty = the quantity): for cards on every
                                   page, where an input name="id" would confuse Shopify's analytics.

   EVENTS (on document unless noted)
     ev:cart:updated   detail { cart, source }     after ANY cart change (ours or Dawn's). cart = /cart.js
     ev:cart:added     detail { item, form }       bubbles from the form after a successful quick add
     ev:cart:error     detail { message, form }    bubbles from the form
     ev:rail:change, ev:tabs:change                bubble from the rail / the tabs wrapper
     ev:reveal                                      bubbles from a [data-ev-reveal] element as it starts to rise

   window.BO
     BO.init(root)     bind everything inside root (call it after you inject markup)
     BO.release(root)  let go of everything inside root (observers, listeners, animations)
     BO.toast(text, { href, linkText, type: 'ok' | 'error', timeout: 5000 })  ->  { el, close() }
     BO.cart.refresh() -> Promise<cart>      BO.cart.get() -> last cart or null
     BO.cart.add(formOrData) -> Promise<item>
     BO.money(cents) -> "$1,895.00"          BO.reduce() -> true when motion is reduced
   ========================================================================================== */
(() => {
  'use strict';
  if (window.BO && window.BO.init) return;

  const W = window, D = document, H = D.documentElement;
  const mq = W.matchMedia('(prefers-reduced-motion: reduce)');
  const reduce = () => mq.matches;
  const hasIO = 'IntersectionObserver' in W;
  const EASE = 'cubic-bezier(.16, 1, .3, 1)';
  const qsa = (root, sel) => {
    const list = Array.prototype.slice.call(root.querySelectorAll(sel));
    if (root !== D && root.matches && root.matches(sel)) list.unshift(root);
    return list;
  };
  const fire = (el, name, detail) => el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
  H.classList.add('ev-js');

  /* ---- the header logo's entrance: play it again when the page's start-up hid it ----
     The entrance (ev-logo.css) starts the moment the logo is first styled. On a refresh the stylesheets come from
     the cache, so it starts at once and runs underneath the page's start-up scripts: Chrome paints only a few of
     its frames (5 or 6 of about 125 with a 4x slower CPU, 01-10) and it looks as if it never played.
     On the first calm frames (at the latest 2 s from here), Chrome's record of slow frames (long-animation-frame)
     says how much of the entrance was frozen (frames of 100 ms and more); when less than half of it was or still
     will be painted, it plays again from the start (not when that moment comes over 4 s after it began). A browser without that record is left alone. At most one replay per page. */
  (() => {
    const svg = D.querySelector('.ev-hdr .ev-logo--animate');
    const PO = W.PerformanceObserver;
    if (!svg || reduce() || D.hidden || svg.closest('.ev-logo-seen') || !PO
      || !(PO.supportedEntryTypes || []).includes('long-animation-frame')) return;
    const LEN = 2500;                                         // the entrance's length (ev-logo.css, EV logo 02-10)
    const running = svg.getAnimations ? svg.getAnimations({ subtree: true }).filter((a) => a.playState === 'running') : [];
    const fcp = performance.getEntriesByName('first-contentful-paint')[0];
    let s = running.reduce((m, a) => (a.startTime != null && a.startTime < m ? a.startTime : m), Infinity);
    if (!isFinite(s)) s = fcp ? fcp.startTime : performance.now() - LEN;
    const slow = [];
    const po = new PO((list) => { slow.push(...list.getEntries()); });
    try { po.observe({ type: 'long-animation-frame', buffered: true }); } catch (e) { return; }
    let done = false, last = 0, calm = 0;
    const decide = () => {
      if (done) return;
      done = true;
      slow.push(...po.takeRecords());
      po.disconnect();
      const t = performance.now(), end = Math.min(s + LEN, t);
      let frozen = 0;
      slow.forEach((e) => {
        if (e.duration < 100) return;                         // 50-100 ms frames still show the motion
        const a = Math.max(e.startTime, s), b = Math.min(e.startTime + e.duration, end);
        if (b > a) frozen += b - a;
      });
      const smooth = Math.max(0, end - s - frozen) + Math.max(0, s + LEN - t);
      H.dataset.boLogoSmooth = Math.round(smooth);             // a hook for checks; no CSS
      // seen enough, or too late: a replay many seconds in reads as a glitch (a 4x slower phone got there at 7-10 s)
      if (smooth >= LEN * 0.5 || t - s > 4000) return;
      svg.classList.remove('ev-logo--animate');
      void getComputedStyle(svg).animationName;               // a style read of the logo only, no layout
      svg.classList.add('ev-logo--animate');
      H.classList.add('ev-logo-replayed');                    // a hook for checks; no CSS
    };
    setTimeout(decide, 2000);
    const tick = (t) => {
      if (done) return;
      calm = last && t - last < 40 ? calm + 1 : 0;
      last = t;
      if (calm >= 3) decide(); else requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  })();

  /* ---- bookkeeping: bind once, let go cleanly ---- */
  const bound = new WeakMap();                       // element -> Set of feature names
  const live = new Set();                            // { el, key, off }
  const once = (el, key) => {
    let s = bound.get(el);
    if (!s) bound.set(el, (s = new Set()));
    if (s.has(key)) return false;
    s.add(key);
    return true;
  };
  const hold = (el, key, off) => live.add({ el, key, off });
  const release = (root) => {
    live.forEach((r) => {
      if (root !== D && !root.contains(r.el) && r.el.isConnected) return;
      try { r.off(); } catch (e) { /* already gone */ }
      const s = bound.get(r.el);
      if (s) s.delete(r.key);
      live.delete(r);
    });
  };

  /* ---- Reveal on scroll (MOTION v2, 02-10: docs/MOTION-V2.md §2; contract: docs/notes/look-motion.md) ----
     [data-ev-reveal] rises 24px and fades in once as it enters (ev-base.css §22); ="stagger" plays its children in
     turn (CSS only). Only what is still below the first screen is hidden (.ev-armed); the first screen shows at
     once (.ev-in.ev-in--now), so nothing there waits and the hero photo is never held back.
     - A reveal INSIDE another reveal rides with the outer one: shown at once, no second animation (a product card,
       which reveals itself, inside a staggered grid or a revealed section).
     - Things that arrive in the same moment (a row of cards) rise in turn: --ev-ri 0..5 (an inline --i wins).
     - A reveal inside a SIDEWAYS rail (.ev-rail__track, [data-ev-track]) waits for its rail, not for its own slot,
       so cards scrolled in sideways are never blank: when the rail arrives, all of them rise in turn.
     Event: ev:reveal (bubbles) on each element as it starts to rise. */
  let revealIO = null, railIO = null;
  const railWait = new Map();                        // rail track -> its armed reveals, in page order
  const showReveal = (el, n) => {
    if (n != null) el.style.setProperty('--ev-ri', n);
    el.classList.remove('ev-armed');
    el.classList.add('ev-in');
    fire(el, 'ev:reveal');
  };
  const reveal = (root) => {
    const els = qsa(root, '[data-ev-reveal]').filter((el) => once(el, 'reveal'));
    if (!els.length) return;
    if (reduce() || !hasIO) { els.forEach((el) => el.classList.add('ev-in', 'ev-in--now')); return; }
    if (!revealIO) {
      revealIO = new IntersectionObserver((entries) => {
        let n = 0;
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          revealIO.unobserve(en.target);
          showReveal(en.target, Math.min(n++, 5));
        });
      }, { rootMargin: '0px 0px -7% 0px', threshold: 0 });
      railIO = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          railIO.unobserve(en.target);
          const list = railWait.get(en.target) || [];
          railWait.delete(en.target);
          list.forEach((el, k) => { if (el.isConnected && el.classList.contains('ev-armed')) showReveal(el, Math.min(k, 5)); });
        });
      }, { rootMargin: '0px 0px -7% 0px', threshold: 0 });
    }
    const vh = W.innerHeight;
    // read every position first, then write: one layout, not one per element
    const tops = els.map((el) => el.getBoundingClientRect().top);
    els.forEach((el, i) => {
      const up = el.parentElement;
      const outer = up && up.closest('[data-ev-reveal]');
      // anything that starts inside the first screen (or above it) shows at once; only what is below the screen
      // waits to rise (Tommy 01-10: the trust strip at the very bottom of the first screen waited for a scroll).
      // A reveal inside another one that is waiting to rise rides with it (one movement, not two). Inside a drawer
      // or a dialog (the cart drawer's suggestions) the panel's own opening is the movement: shown at once.
      if (tops[i] < vh || (outer && outer.classList.contains('ev-armed')) || el.closest('cart-drawer, .drawer, dialog, [role="dialog"], [data-ev-reveal-off]')) {
        el.classList.add('ev-in', 'ev-in--now');
        return;
      }
      el.classList.add('ev-armed');
      const rail = up && up.closest('.ev-rail__track, [data-ev-track]');
      if (rail) {
        let list = railWait.get(rail);
        if (!list) { railWait.set(rail, (list = [])); railIO.observe(rail); }
        list.push(el);
        hold(el, 'reveal', () => { el.classList.remove('ev-armed'); railWait.delete(rail); railIO.unobserve(rail); });
        return;
      }
      revealIO.observe(el);
      hold(el, 'reveal', () => { revealIO.unobserve(el); el.classList.remove('ev-armed'); });
    });
  };

  /* ---- Sections near the screen: pause the rest (ev-base.css) ---- */
  let viewIO = null;
  const inView = (root) => {
    const secs = qsa(root, '.shopify-section');
    if (!secs.length) return;
    if (!hasIO) { secs.forEach((s) => s.classList.add('ev-in-view')); return; }
    if (!viewIO) {
      viewIO = new IntersectionObserver((entries) => {
        entries.forEach((en) => en.target.classList.toggle('ev-in-view', en.isIntersecting));
        H.classList.add('ev-io');
      }, { rootMargin: '200px 0px 200px 0px' });
    }
    secs.forEach((s) => {
      // never paused: the header group, and a section with no height of its own (all of it is fixed or
      // hidden: a pop-up, a drawer), which an observer would always report as off screen
      if (s.classList.contains('shopify-section-group-header-group') || !s.offsetHeight) { s.classList.add('ev-in-view'); return; }
      if (!once(s, 'view')) return;
      viewIO.observe(s);
      hold(s, 'view', () => viewIO.unobserve(s));
    });
  };

  /* ---- Rails ----
     Cheap by design (a home page holds a dozen rails): the geometry is read once, and again only when a
     track's WIDTH changes (images loading change its height, which does not matter). All rails that need a
     re-measure are read together in one frame, then written together, so the page lays out once, not once per
     rail. A scroll frame reads nothing but scrollLeft and writes only what changed: the visible cards, the page
     and the arrows' state come from the stored geometry (the cards of a rail are all one width). */
  const railDirty = new Set();
  let railRaf = 0;
  const railFlush = () => {
    railRaf = 0;
    const list = Array.from(railDirty);
    railDirty.clear();
    list.forEach((r) => r.measure());          // reads, all together (scrollLeft too: see update)
    list.forEach((r) => r.update(true));       // writes, all together, reading nothing (a rail's writes would
                                               // otherwise make the next rail's scrollLeft lay the page out again:
                                               // 0.76 s on the home page with a 4x slower phone CPU, 01-10)
  };
  const railAsk = (r) => { railDirty.add(r); if (!railRaf) railRaf = requestAnimationFrame(railFlush); };
  const rails = (root) => {
    qsa(root, '[data-ev-rail]').forEach((rail) => {
      const track = rail.querySelector('[data-ev-track], .ev-rail__track');
      if (!track || !once(rail, 'rail')) return;
      const prev = rail.querySelector('[data-ev-prev]');
      const next = rail.querySelector('[data-ev-next]');
      const dots = rail.querySelector('[data-ev-dots]');
      const count = rail.querySelector('[data-ev-rail-count]');
      const wantsP = rail.hasAttribute('data-ev-rail-p');
      const pattern = (count && count.getAttribute('data-ev-rail-count')) || '[first] - [last] / [total]';
      let raf = 0, lastP = -1, lastPages = -1, lastPage = -1, lastStill = null, lastPrev = null, lastNext = null, lastCount = '', lastW = -1;
      let geo = { view: 0, max: 0, pages: 1, stride: 1, item: 1, gap: 0, padL: 0, n: 0 };
      const setOff = (btn, off) => {
        if (!btn) return;
        if (off) btn.setAttribute('aria-disabled', 'true'); else btn.removeAttribute('aria-disabled');
      };
      // one page = the cards that fit WHOLE in the track (4 of 4, 2 of 2.6, 1 of 1.6)
      const measure = () => {
        const view = track.clientWidth;
        const max = track.scrollWidth - view;
        const first = track.firstElementChild;
        const cs = getComputedStyle(track);
        const gap = parseFloat(cs.columnGap) || 0;
        const padL = parseFloat(cs.paddingLeft) || 0;
        const pad = padL + (parseFloat(cs.paddingRight) || 0);
        const iw = first ? first.getBoundingClientRect().width : view;
        const per = Math.max(1, Math.floor((view - pad + gap + 1) / Math.max(1, iw + gap)));
        const n = track.children.length;
        geo = { view, max, pages: max > 1 ? Math.ceil(n / per) : 1, stride: per * (iw + gap), item: iw, gap, padL, n, x: Math.abs(track.scrollLeft) };
      };
      const update = (stored) => {
        raf = 0;
        const view = geo.view, max = geo.max, pages = geo.pages;
        const x = stored === true ? geo.x : Math.abs(track.scrollLeft);
        const p = max > 1 ? clamp(x / max, 0, 1) : 0;
        const page = pages < 2 ? 0 : x >= max - 2 ? pages - 1 : clamp(Math.round(x / geo.stride), 0, pages - 1);
        if (wantsP && Math.abs(p - lastP) > 0.001) rail.style.setProperty('--ev-rail-p', p.toFixed(4));
        const still = max <= 1, offPrev = x <= 2, offNext = x >= max - 2;
        if (still !== lastStill) { rail.classList.toggle('is-still', still); lastStill = still; }
        if (offPrev !== lastPrev) { setOff(prev, offPrev); lastPrev = offPrev; }
        if (offNext !== lastNext) { setOff(next, offNext); lastNext = offNext; }
        if (dots) {
          if (pages !== lastPages) {
            dots.textContent = '';
            const show = pages > 1 && pages <= 12;
            dots.hidden = !show;
            if (show) for (let i = 0; i < pages; i++) dots.appendChild(D.createElement('i'));
            lastPage = -1;
          }
          if (page !== lastPage && dots.children.length) {
            for (let i = 0; i < dots.children.length; i++) dots.children[i].classList.toggle('is-on', i === page);
          }
        }
        if (count && geo.n) {
          // card i's middle sits at padL + i * (item + gap) + item / 2 inside the scrolled track
          const step = Math.max(1, geo.item + geo.gap);
          let first = 0, last = 0;
          for (let i = 0; i < geo.n; i++) {
            const mid = geo.padL + i * step + geo.item / 2 - x;
            if (mid > 0 && mid < view) { if (!first) first = i + 1; last = i + 1; }
          }
          const txt = pattern.replace('[first]', first).replace('[last]', last).replace('[total]', geo.n);
          if (txt !== lastCount) { count.textContent = txt; lastCount = txt; }
        }
        if (Math.abs(p - lastP) > 0.001 || pages !== lastPages || page !== lastPage) {
          lastP = p; lastPages = pages; lastPage = page;
          fire(rail, 'ev:rail:change', { progress: p, page, pages });
        }
      };
      const self = { measure, update };
      const ask = () => { if (!raf) raf = requestAnimationFrame(update); };
      // re-measure only when the track's width changed (or cards were added / removed)
      const resized = (entries) => {
        const e = entries && entries[0];
        const w = e && e.contentRect ? Math.round(e.contentRect.width) : -2;
        if (w === lastW && track.children.length === geo.n) return;
        lastW = w;
        railAsk(self);
      };
      const go = (dir) => track.scrollBy({ left: dir * Math.max(geo.stride, 120), behavior: reduce() ? 'auto' : 'smooth' });
      const onPrev = (e) => { e.preventDefault(); if (prev.getAttribute('aria-disabled') !== 'true') go(-1); };
      const onNext = (e) => { e.preventDefault(); if (next.getAttribute('aria-disabled') !== 'true') go(1); };
      if (prev) prev.addEventListener('click', onPrev);
      if (next) next.addEventListener('click', onNext);
      track.addEventListener('scroll', ask, { passive: true });
      const onWinResize = () => railAsk(self);
      const ro = 'ResizeObserver' in W ? new ResizeObserver(resized) : null;
      if (ro) ro.observe(track); else W.addEventListener('resize', onWinResize);
      railAsk(self);                            // first measure: in the shared frame with every other rail
      hold(rail, 'rail', () => {
        if (prev) prev.removeEventListener('click', onPrev);
        if (next) next.removeEventListener('click', onNext);
        track.removeEventListener('scroll', ask);
        if (ro) ro.disconnect(); else W.removeEventListener('resize', onWinResize);
        if (raf) cancelAnimationFrame(raf);
        railDirty.delete(self);
      });
    });
  };

  /* ---- Tabs ---- */
  const tabs = (root) => {
    qsa(root, '[data-ev-tabs]').forEach((wrap) => {
      if (!once(wrap, 'tabs')) return;
      const list = Array.prototype.slice.call(wrap.querySelectorAll('[role="tab"]'));
      if (!list.length) return;
      const bar = list[0].closest('[role="tablist"]') || list[0].parentElement;
      const panelOf = (t) => { const id = t.getAttribute('aria-controls'); return id ? D.getElementById(id) : null; };
      // more tabs than fit: mark which end still has tabs out of sight (ev-base.css fades that end)
      const more = () => {
        if (!bar) return;
        const max = bar.scrollWidth - bar.clientWidth, x = Math.abs(bar.scrollLeft);
        bar.classList.toggle('is-more-start', max > 2 && x > 2);
        bar.classList.toggle('is-more-end', max > 2 && x < max - 2);
      };
      const key = (instant) => {                       // the sliding key behind the chosen tab
        const on = list.filter((t) => t.getAttribute('aria-selected') === 'true')[0];
        if (!on || !bar) return;
        bar.classList.toggle('is-instant', !!instant || reduce());
        bar.style.setProperty('--ev-tab-x', on.offsetLeft + 'px');
        bar.style.setProperty('--ev-tab-w', on.offsetWidth + 'px');
        bar.classList.add('has-key');
        more();
      };
      const show = (t, focus) => {
        list.forEach((x) => {
          const on = x === t;
          x.setAttribute('aria-selected', on ? 'true' : 'false');
          x.tabIndex = on ? 0 : -1;
          const p = panelOf(x);
          if (p) p.hidden = !on;
        });
        const p = panelOf(t);
        key(false);
        if (focus) t.focus();
        if (t.scrollIntoView && bar && bar.scrollWidth > bar.clientWidth) t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduce() ? 'auto' : 'smooth' });
        if (p) init(p);                                // rails, reveals and counters inside a panel that was hidden
        fire(wrap, 'ev:tabs:change', { tab: t, panel: p });
      };
      const onClick = (e) => { const t = e.target.closest('[role="tab"]'); if (t && list.indexOf(t) > -1) show(t, false); };
      const onKey = (e) => {
        const i = list.indexOf(e.target.closest('[role="tab"]'));
        if (i < 0) return;
        let n = -1;
        if (e.key === 'ArrowRight') n = (i + 1) % list.length;
        else if (e.key === 'ArrowLeft') n = (i - 1 + list.length) % list.length;
        else if (e.key === 'Home') n = 0;
        else if (e.key === 'End') n = list.length - 1;
        if (n > -1) { e.preventDefault(); show(list[n], true); }
      };
      wrap.addEventListener('click', onClick);
      wrap.addEventListener('keydown', onKey);
      if (bar) bar.addEventListener('scroll', more, { passive: true });
      // first paint: make the markup consistent, whatever the section wrote
      const first = list.filter((t) => t.getAttribute('aria-selected') === 'true')[0] || list[0];
      list.forEach((x) => { const on = x === first; x.setAttribute('aria-selected', on ? 'true' : 'false'); x.tabIndex = on ? 0 : -1; const p = panelOf(x); if (p) p.hidden = !on; });
      key(true);
      const ro = 'ResizeObserver' in W && bar ? new ResizeObserver(() => key(true)) : null;
      if (ro) ro.observe(bar);
      if (D.fonts && D.fonts.ready) D.fonts.ready.then(() => key(true));
      wrap.boShowTab = (t) => show(t, false);
      hold(wrap, 'tabs', () => { wrap.removeEventListener('click', onClick); wrap.removeEventListener('keydown', onKey); if (bar) bar.removeEventListener('scroll', more); if (ro) ro.disconnect(); });
    });
  };

  /* ---- Accordions: <details> that open and close smoothly; optionally one at a time ---- */
  const cssOpens = !!(W.CSS && CSS.supports && CSS.supports('interpolate-size', 'allow-keywords'));   // then ev-base.css animates it
  const accordions = (root) => {
    qsa(root, '[data-ev-accordion]').forEach((wrap) => {
      if (!once(wrap, 'accordion')) return;
      const single = wrap.getAttribute('data-ev-accordion') === 'single';
      const busy = new WeakSet();
      const slide = (d, body, opening) => {
        const cs = getComputedStyle(body);
        const full = { height: body.offsetHeight + 'px', paddingTop: cs.paddingTop, paddingBottom: cs.paddingBottom, opacity: 1 };
        const shut = { height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 };
        body.style.overflow = 'hidden';
        busy.add(d);
        const a = body.animate(opening ? [shut, full] : [full, shut], { duration: opening ? 480 : 300, easing: EASE });
        const done = () => { if (!opening) d.open = false; body.style.overflow = ''; busy.delete(d); };
        a.onfinish = done;
        a.oncancel = done;
      };
      const onClick = (e) => {
        const sum = e.target.closest('summary');
        if (!sum || !wrap.contains(sum)) return;
        const d = sum.parentElement;
        const body = sum.nextElementSibling;
        if (!(d instanceof HTMLDetailsElement) || cssOpens || reduce() || !body || !body.animate) return;   // the browser does it
        e.preventDefault();
        if (busy.has(d)) return;
        if (d.open) { slide(d, body, false); return; }
        d.open = true;
        slide(d, body, true);
        if (single) shutOthers(d);
      };
      const shutOthers = (d) => {
        wrap.querySelectorAll('details[open]').forEach((o) => {
          if (o === d || o.contains(d) || d.contains(o)) return;
          const body = o.querySelector(':scope > summary + *');
          if (!cssOpens && !reduce() && body && body.animate && !busy.has(o)) slide(o, body, false); else o.open = false;
        });
      };
      const onToggle = (e) => { const d = e.target; if (single && d instanceof HTMLDetailsElement && d.open && (cssOpens || reduce())) shutOthers(d); };
      wrap.addEventListener('click', onClick);
      wrap.addEventListener('toggle', onToggle, true);   // toggle does not bubble
      hold(wrap, 'accordion', () => { wrap.removeEventListener('click', onClick); wrap.removeEventListener('toggle', onToggle, true); });
    });
  };

  /* ---- Marquee: hold, ease into the loop; ease to a stop under the pointer ----
     The track itself is moved, one transform a frame, and only while it is moving (no frames are spent on a
     strip that is off screen, held, or stopped under the pointer). */
  const marquee = (root) => {
    qsa(root, '[data-ev-marquee]').forEach((m) => {
      const tr = m.querySelector('.ev-marquee__track');
      if (!tr || !once(m, 'marquee')) return;
      const originals = Array.prototype.slice.call(tr.children);
      if (reduce() || !hasIO || originals.length < 2) { m.classList.add('is-static'); return; }
      const speed = parseFloat(m.getAttribute('data-ev-speed')) || 48;
      const wait = m.hasAttribute('data-ev-hold') ? parseFloat(m.getAttribute('data-ev-hold')) || 0 : 2500;
      let x = 0, loop = 0, rate = 0, target = 0, raf = 0, last = 0, timer = 0, rz = 0;
      let seen = false, started = false, hover = false, focus = false, width = 0;

      const place = () => { tr.style.transform = x ? 'translate3d(' + (-x).toFixed(2) + 'px,0,0)' : ''; };
      const build = () => {
        const at = loop > 0 ? x / loop : 0;
        tr.style.transform = '';
        tr.querySelectorAll('[data-ev-clone]').forEach((n) => n.remove());
        m.scrollLeft = 0;
        width = m.clientWidth;
        loop = 0;
        const one = originals[originals.length - 1].getBoundingClientRect().right - originals[0].getBoundingClientRect().left;
        if (!(one > 0)) return;
        const copies = Math.min(8, Math.ceil(width / one) + 1);
        const frag = D.createDocumentFragment();
        for (let c = 0; c < copies; c++) {
          originals.forEach((n) => {
            const k = n.cloneNode(true);
            k.setAttribute('aria-hidden', 'true');
            k.setAttribute('data-ev-clone', '');
            k.querySelectorAll('a, button, input, [tabindex]').forEach((f) => f.setAttribute('tabindex', '-1'));
            if (k.matches('a, button')) k.setAttribute('tabindex', '-1');
            frag.appendChild(k);
          });
        }
        tr.appendChild(frag);
        loop = tr.children[originals.length].getBoundingClientRect().left - originals[0].getBoundingClientRect().left;
        if (!(loop > 0)) { loop = 0; return; }
        x = at * loop;
        place();
        m.classList.add('is-ready');
        aim();
      };
      const step = (now) => {
        raf = 0;
        const dt = Math.min((now - last) / 1000, 0.05);
        last = now;
        rate += (target - rate) * (1 - Math.exp(-dt * (target > rate ? 1.6 : 5)));    // eases in gently, stops promptly
        if (Math.abs(target - rate) < 0.004) rate = target;
        if (rate > 0 && loop > 0) { x = (x + speed * rate * dt) % loop; place(); }
        if (rate > 0 || target > 0) raf = requestAnimationFrame(step);
      };
      const aim = () => {
        target = loop > 0 && seen && started && !hover && !focus && !D.hidden ? 1 : 0;
        if (!raf && (rate !== target || target > 0)) { last = performance.now(); raf = requestAnimationFrame(step); }
      };
      const io = new IntersectionObserver((es) => {
        seen = es[0].isIntersecting;
        if (seen && !started && !timer) timer = setTimeout(() => { started = true; aim(); }, wait);
        aim();
      }, { threshold: 0.05 });
      const enter = (e) => { if (e.pointerType === 'mouse' || !e.pointerType) { hover = true; aim(); } };
      const leave = () => { hover = false; aim(); };
      const fin = () => { focus = true; aim(); };
      const fout = () => { focus = false; aim(); };
      const vis = () => aim();
      const resize = () => { clearTimeout(rz); rz = setTimeout(() => { if (Math.abs(m.clientWidth - width) > 1) build(); }, 160); };
      m.addEventListener('pointerenter', enter);
      m.addEventListener('pointerleave', leave);
      m.addEventListener('focusin', fin);
      m.addEventListener('focusout', fout);
      D.addEventListener('visibilitychange', vis);
      W.addEventListener('resize', resize);
      const start = () => { build(); io.observe(m); };
      if (D.fonts && D.fonts.ready) D.fonts.ready.then(start); else start();
      hold(m, 'marquee', () => {
        io.disconnect(); clearTimeout(timer); clearTimeout(rz); if (raf) cancelAnimationFrame(raf);
        m.removeEventListener('pointerenter', enter); m.removeEventListener('pointerleave', leave);
        m.removeEventListener('focusin', fin); m.removeEventListener('focusout', fout);
        D.removeEventListener('visibilitychange', vis); W.removeEventListener('resize', resize);
        tr.style.transform = '';
        tr.querySelectorAll('[data-ev-clone]').forEach((n) => n.remove());
        m.classList.remove('is-ready');
      });
    });
  };

  /* ---- Count up (real numbers only: the target is whatever the section wrote in data-ev-count) ---- */
  const countUp = (root) => {
    qsa(root, '[data-ev-count]').forEach((el) => {
      if (!once(el, 'count')) return;
      const raw = String(el.getAttribute('data-ev-count') || '0').replace(/,/g, '');
      const end = parseFloat(raw) || 0;
      const dp = (raw.split('.')[1] || '').length;
      const pre = el.getAttribute('data-ev-prefix') || '', suf = el.getAttribute('data-ev-suffix') || '';
      const fmt = (n) => pre + n.toLocaleString('en-AU', { minimumFractionDigits: dp, maximumFractionDigits: dp }) + suf;
      if (reduce() || !hasIO) { el.textContent = fmt(end); return; }
      const ob = new IntersectionObserver((es) => {
        if (!es[0].isIntersecting) return;
        ob.disconnect();
        const t0 = performance.now(), dur = 1600;
        const tick = (t) => {
          const p = Math.min(1, (t - t0) / dur);
          el.textContent = fmt(p < 1 ? end * (1 - Math.pow(2, -10 * p)) : end);      // the same expo-out as --ev-ease
          if (p < 1 && el.isConnected) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }, { threshold: 0.4 });
      ob.observe(el);
      hold(el, 'count', () => ob.disconnect());
    });
  };

  /* ---- Parallax + html.ev-scrolled ---- */
  let para = [], ticking = false;
  const parallax = (root) => {
    if (reduce()) return;
    qsa(root, '[data-ev-parallax]').forEach((el) => { if (once(el, 'parallax')) para.push(el); });
  };
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      H.classList.toggle('ev-scrolled', W.scrollY > 40);
      if (!para.length) return;
      para = para.filter((el) => el.isConnected);
      const vh = W.innerHeight;
      const rects = para.map((el) => (el.offsetParent === null ? null : el.getBoundingClientRect()));
      rects.forEach((r, i) => {
        if (!r) return;
        const el = para[i];
        const k = parseFloat(el.getAttribute('data-ev-parallax')) || 0.1;
        const own = parseFloat(el.style.getPropertyValue('--ev-py')) || 0;
        const top = r.top - own;                                   // where it would be without the shift
        if (top + r.height < -200 || top > vh + 200) return;
        el.style.setProperty('--ev-py', ((top + r.height / 2 - vh / 2) * -k).toFixed(1) + 'px');
      });
    });
  };
  W.addEventListener('scroll', onScroll, { passive: true });
  W.addEventListener('resize', onScroll);

  /* ---- Lite YouTube ---- */
  const youtube = (root) => {
    qsa(root, '[data-ev-yt]').forEach((b) => {
      if (!once(b, 'yt')) return;
      b.addEventListener('click', () => {
        const id = b.getAttribute('data-ev-yt');
        if (!id) return;
        const f = D.createElement('iframe');
        f.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0&modestbranding=1&playsinline=1';
        f.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        f.allowFullscreen = true;
        f.title = (b.getAttribute('aria-label') || 'YouTube video').replace(/^play( video)?:?\s*/i, '');
        f.className = 'ev-yt-frame';
        b.replaceWith(f);
        f.focus();
      }, { once: true });
    });
  };

  /* ---- Quantity stepper: [data-ev-qty="1"] / [data-ev-qty="-1"] inside .ev-qty (one delegated listener) ---- */
  D.addEventListener('click', (e) => {
    const b = e.target.closest && e.target.closest('[data-ev-qty]');
    if (!b || b.disabled) return;
    const wrap = b.closest('.ev-qty') || b.parentElement;
    const input = wrap && wrap.querySelector('input');
    if (!input) return;
    const by = (parseFloat(b.getAttribute('data-ev-qty')) || 0) * (parseFloat(input.step) || 1);
    const min = input.min !== '' ? parseFloat(input.min) : 1;
    const max = input.max !== '' ? parseFloat(input.max) : Infinity;
    const v = clamp((parseFloat(input.value) || 0) + by, min, max);
    if (String(v) === input.value) return;
    input.value = String(v);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---- Toast ---- */
  const ICON_OK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4.5 12.5l5 5L19.5 7"/></svg>';
  const ICON_X = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M5.5 5.5l13 13M18.5 5.5l-13 13"/></svg>';
  const ICON_BANG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M12 5.5v8M12 18h.01"/></svg>';
  let toastHost = null;
  const toast = (text, opts) => {
    const o = opts || {};
    if (!toastHost || !toastHost.isConnected) {
      toastHost = D.createElement('div');
      toastHost.className = 'ev-toasts';
      D.body.appendChild(toastHost);
    }
    const el = D.createElement('div');
    const bad = o.type === 'error';
    el.className = 'ev-toast' + (bad ? ' ev-toast--error' : '');
    el.setAttribute('role', bad ? 'alert' : 'status');
    const ic = D.createElement('span');
    ic.className = 'ev-toast__icon';
    ic.innerHTML = bad ? ICON_BANG : ICON_OK;
    const tx = D.createElement('span');
    tx.className = 'ev-toast__text';
    tx.textContent = String(text == null ? '' : text);
    el.append(ic, tx);
    if (o.href) {
      const a = D.createElement('a');
      a.className = 'ev-toast__link';
      a.href = o.href;
      a.textContent = o.linkText || 'View';
      el.appendChild(a);
    }
    const x = D.createElement('button');
    x.type = 'button';
    x.className = 'ev-toast__close';
    x.setAttribute('aria-label', o.closeLabel || 'Dismiss');
    x.innerHTML = ICON_X;
    el.appendChild(x);
    let timer = 0, gone = false;
    const close = () => {
      if (gone) return;
      gone = true;
      clearTimeout(timer);
      el.classList.add('is-leaving');
      setTimeout(() => el.remove(), reduce() ? 0 : 380);
    };
    const arm = () => { clearTimeout(timer); if (o.timeout !== 0) timer = setTimeout(close, o.timeout || 5000); };
    x.addEventListener('click', close);
    el.addEventListener('pointerenter', () => clearTimeout(timer));
    el.addEventListener('pointerleave', arm);
    el.addEventListener('focusin', () => clearTimeout(timer));
    el.addEventListener('focusout', arm);
    while (toastHost.children.length > 2) toastHost.firstElementChild.remove();
    toastHost.appendChild(el);
    arm();
    return { el, close };
  };

  /* ---- Money ---- */
  const money = (cents) => {
    const cur = (W.Shopify && W.Shopify.currency && W.Shopify.currency.active) || 'AUD';
    try {
      return new Intl.NumberFormat('en-AU', { style: 'currency', currency: cur, currencyDisplay: 'narrowSymbol' }).format((Number(cents) || 0) / 100);
    } catch (e) {
      return '$' + ((Number(cents) || 0) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  };

  /* ---- Cart: one bus. Whoever changes the cart, everyone hears "ev:cart:updated". ---- */
  const cart = (() => {
    let lastCart = null, pending = null;
    const route = (k, fallback) => (W.routes && W.routes[k]) || fallback;
    const url = () => String(route('cart_url', '/cart')).replace(/\/$/, '') + '.js';
    const bump = (el) => {
      if (reduce()) return;
      el.classList.remove('is-bump');
      void el.offsetWidth;
      el.classList.add('is-bump');
      setTimeout(() => el.classList.remove('is-bump'), 650);
    };
    const paint = (c) => {
      D.querySelectorAll('[data-ev-cart-count]').forEach((el) => {
        const n = String(c.item_count);
        if (el.textContent.trim() !== n) { el.textContent = n; bump(el); }
        if (el.hasAttribute('data-ev-hide-empty')) el.hidden = c.item_count === 0;
      });
      D.querySelectorAll('[data-ev-cart-total]').forEach((el) => { el.textContent = money(c.total_price); });
    };
    const emit = (c, source) => {
      lastCart = c;
      paint(c);
      fire(D, 'ev:cart:updated', { cart: c, source: source || 'refresh' });
      return c;
    };
    const refresh = (source) => {
      if (!pending) {
        pending = fetch(url(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
          .then((r) => r.json())
          .then((c) => { pending = null; return emit(c, source); })
          .catch((e) => { pending = null; throw e; });
      }
      return pending;
    };
    const dawnCart = () => D.querySelector('cart-drawer') || D.querySelector('cart-notification');
    // Dawn's own cart icon, when no Dawn drawer / notification re-rendered it for us
    const dawnBubble = () => {
      const holder = D.getElementById('cart-icon-bubble');
      if (!holder) return;
      const rootUrl = (W.Shopify && W.Shopify.routes && W.Shopify.routes.root) || '/';
      fetch(rootUrl + '?sections=cart-icon-bubble', { credentials: 'same-origin' })
        .then((r) => r.json())
        .then((s) => {
          const html = s && s['cart-icon-bubble'];
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('.shopify-section') || doc.body;
          holder.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    };

    // POST one line. `input` is a form, a FormData, or { id, quantity }.
    const add = (input, form) => {
      let body;
      if (input instanceof HTMLFormElement) body = new FormData(input);
      else if (input instanceof FormData) body = input;
      else { body = new FormData(); Object.keys(input || {}).forEach((k) => body.append(k, input[k])); }
      // phone review 02-10: on the cart page the page is the cart: no drawer over it. Dawn re-draws the lines and
      // ev-cart.js the summary; a short note says it was added.
      const ui = D.body.classList.contains('ev-page-cart') ? null : dawnCart();
      const canRender = !!(ui && typeof ui.renderContents === 'function' && typeof ui.getSectionsToRender === 'function');
      if (canRender) {
        try {
          body.append('sections', ui.getSectionsToRender().map((s) => s.id));
          body.append('sections_url', W.location.pathname);
          if (typeof ui.setActiveElement === 'function') ui.setActiveElement(D.activeElement);
        } catch (e) { /* fall through to the toast */ }
      }
      const addUrl = String(route('cart_add_url', '/cart/add')).replace(/\.js$/, '') + '.js';
      return fetch(addUrl, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' }, body, credentials: 'same-origin' })
        .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || data.status) {
            const err = new Error(data.description || data.message || 'Could not add to cart');
            err.boCart = true;
            throw err;
          }
          // keep Dawn's own listeners in step (cart page, quick order list, price per item)
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ev-quick-add', productVariantId: body.get('id'), cartData: data }); } catch (e) { /* not fatal */ }
          }
          let shown = false;
          if (canRender && data.sections) {
            try {
              if (ui.classList.contains('is-empty')) ui.classList.remove('is-empty');
              ui.renderContents(data);
              shown = true;
            } catch (e) { shown = false; }
          }
          if (!shown) {
            const words = (form && form.getAttribute('data-msg-ok')) || 'Added to your cart';
            toast(words, { href: route('cart_url', '/cart'), linkText: (form && form.getAttribute('data-msg-view')) || 'View cart' });
            dawnBubble();
          }
          refresh('ev-quick-add').catch(() => {});
          return data;
        });
    };
    return { refresh, add, emit, get: () => lastCart };
  })();

  // Dawn changed the cart (drawer quantity, product form, cart page): tell everyone.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    try {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (ev) => {
        if (ev && ev.source === 'ev-quick-add') return;
        cart.refresh('dawn').catch(() => {});
      });
    } catch (e) { /* Dawn's pubsub is not there: nothing to mirror */ }
  }
  // Back / forward cache shows a stale count: ask again.
  W.addEventListener('pageshow', (e) => { if (e.persisted) cart.refresh('pageshow').catch(() => {}); });

  /* ---- Quick add: every form[data-ev-quick-add], now and later (one delegated listener) ----
     The same for a holder with no form (snippets/ev-product-card add_mode 'button'):
     <div data-ev-quick-add data-ev-variant="123" data-ev-add-qty="1" data-msg-ok…><button type="button" data-ev-add> */
  D.addEventListener('submit', (evt) => {
    const form = evt.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-ev-quick-add]')) return;
    evt.preventDefault();
    quickAdd(form, form.querySelector('[type="submit"]'), form);
  });
  D.addEventListener('click', (evt) => {
    const btn = evt.target.closest && evt.target.closest('[data-ev-add]');
    if (!btn) return;
    const holder = btn.closest('[data-ev-quick-add][data-ev-variant]');
    if (!holder) return;
    evt.preventDefault();
    quickAdd(holder, btn, { id: holder.getAttribute('data-ev-variant'), quantity: holder.getAttribute('data-ev-add-qty') || '1' });
  });
  function quickAdd(form, btn, input) {
    if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
    const msg = form.querySelector('[data-ev-cart-msg]');
    const say = (t) => { if (msg) msg.textContent = t || ''; };
    btn.setAttribute('aria-disabled', 'true');
    btn.classList.remove('is-added');
    btn.classList.add('is-loading');
    say('');
    cart.add(input, form).then((item) => {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-disabled');
      btn.classList.add('is-added');
      clearTimeout(btn.boAddedTimer);
      btn.boAddedTimer = setTimeout(() => btn.classList.remove('is-added'), 2400);
      fire(form, 'ev:cart:added', { item, form });
    }).catch((e) => {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-disabled');
      const words = (e && e.boCart && e.message) || form.getAttribute('data-msg-error') || 'Sorry, that could not be added. Please try again.';
      if (msg) say(words); else toast(words, { type: 'error' });
      fire(form, 'ev:cart:error', { message: words, form });
    });
  }

  /* ---- Theme editor: bring the chosen block into view ---- */
  D.addEventListener('shopify:block:select', (e) => {
    const el = e.target;
    const panel = el.closest && el.closest('[role="tabpanel"][hidden]');
    if (panel) {
      const wrap = panel.closest('[data-ev-tabs]');
      const tab = wrap && wrap.querySelector('[role="tab"][aria-controls="' + panel.id + '"]');
      if (tab && wrap.boShowTab) wrap.boShowTab(tab);
    }
    const det = el.closest && el.closest('details:not([open])');
    if (det) det.open = true;
    const track = el.closest && el.closest('.ev-rail__track, [data-ev-track]');
    if (track) {
      const item = Array.prototype.slice.call(track.children).filter((c) => c === el || c.contains(el))[0];
      if (item) track.scrollTo({ left: item.offsetLeft - track.offsetLeft, behavior: reduce() ? 'auto' : 'smooth' });
    }
  });

  /* ---- Free-shipping bar (snippets/ev-cart-freeship.liquid; MOTION v2 §7, 02-10) ----
     Dawn redraws the cart drawer (and the cart page's summary) after every change, so a new bar arrives showing its
     final value. It is put back where the last bar stood and FILLS smoothly to its new value (transform only, the
     snippet's --ev-ship-p); crossing into free shipping plays a small burst once. A page that opens with an empty
     cart starts from 0. Reduced motion: the bar simply shows its value. */
  let shipP = 0, shipBoot = true;
  const shipSeen = new WeakSet();
  const shipBurst = (box) => {
    const b = box.querySelector('.ev-ship__burst');
    if (!b || !box.isConnected) return;
    if (!b.childElementCount) {
      for (let k = 0; k < 12; k++) {
        const a = (k / 12) * Math.PI * 2 + (k % 2) * 0.22, r = 22 + (k % 3) * 9;
        const i = D.createElement('i');
        i.style.setProperty('--x', (Math.cos(a) * r).toFixed(1) + 'px');
        i.style.setProperty('--y', (Math.sin(a) * r * 0.75 - 6).toFixed(1) + 'px');
        i.style.setProperty('--d', (k % 4) * 30 + 'ms');
        b.appendChild(i);
      }
    }
    box.classList.remove('is-burst');
    void b.offsetWidth;                                // restart the animation (a read of one small element)
    box.classList.add('is-burst');
  };
  const shipBind = (root) => {
    const boxes = qsa(root, '[data-ev-ship]').filter((b) => !shipSeen.has(b));
    if (!boxes.length) return;
    const from = shipP;
    let to = from;
    boxes.forEach((box) => {
      shipSeen.add(box);
      const p = clamp((parseFloat(box.getAttribute('data-ev-ship-p')) || 0) / 100, 0, 1);
      to = p;
      const fill = box.querySelector('.ev-ship__fill');
      if (shipBoot || !fill || reduce() || Math.abs(from - p) < 0.002) return;
      fill.style.transition = 'none';
      fill.style.setProperty('--ev-ship-p', from);
      void getComputedStyle(fill).transform;          // commit the start (a style read of the bar only)
      fill.style.transition = '';
      fill.style.transitionDelay = '180ms';
      requestAnimationFrame(() => fill.style.setProperty('--ev-ship-p', p));
      if (from < 1 && p >= 1) {
        // the burst when the fill arrives (or after 1.5 s, whichever comes first)
        let done = false;
        const go = () => { if (!done) { done = true; shipBurst(box); } };
        fill.addEventListener('transitionend', (e) => { if (e.propertyName === 'transform') go(); }, { once: true });
        setTimeout(go, 1500);
      }
    });
    shipP = to;
  };
  const shipWatch = () => {
    shipBind(D);
    shipBoot = false;
    if (!W.MutationObserver) return;
    const mo = new MutationObserver((list) => {
      for (const m of list) { if (m.addedNodes.length) { shipBind(D); return; } }
    });
    D.querySelectorAll('cart-drawer, #main-cart-footer, [id^="shopify-section"][id*="main-cart-footer"]').forEach((el) => mo.observe(el, { childList: true, subtree: true }));
  };

  /* ---- Card colour dots (Tommy 05-10): show every colour that fits on the one line; "+N" only for the rest ---- */
  const swFitRow = (row) => {
    const dots = row.querySelectorAll('.ev-card__dot');
    const more = row.querySelector('[data-ev-sw-more]');
    dots.forEach((d) => { d.hidden = false; });
    if (more) more.hidden = true;
    if (!row.clientWidth || row.scrollWidth <= row.clientWidth + 1) return;
    let shown = dots.length;
    if (more) more.hidden = false;
    do {
      shown -= 1;
      dots[shown].hidden = true;
      if (more) more.textContent = '+' + (dots.length - shown);
    } while (shown > 1 && row.scrollWidth > row.clientWidth + 1);
  };
  const swRo = 'ResizeObserver' in W ? new ResizeObserver((list) => list.forEach((e) => { try { swFitRow(e.target); } catch (err) { /* decoration only */ } })) : null;
  const swFit = (r) => {
    r.querySelectorAll('[data-ev-sw-fit]').forEach((row) => {
      try { swFitRow(row); } catch (err) { /* decoration only */ }
      if (swRo && !row.dataset.evSwBound) { row.dataset.evSwBound = '1'; swRo.observe(row); }
    });
  };

  /* ---- Boot ---- */
  function init(root) {
    const r = root && root.querySelectorAll ? root : D;
    reveal(r); inView(r); rails(r); tabs(r); accordions(r); marquee(r); countUp(r); parallax(r); youtube(r); swFit(r);
    onScroll();
  }
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', shipWatch); else shipWatch();
  W.BO = { init, release, toast, cart, money, reduce };
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', () => init(D)); else init(D);
  D.addEventListener('shopify:section:load', (e) => init(e.target));
  D.addEventListener('shopify:section:unload', (e) => release(e.target));
})();
